import type AnalyticsWebClient from '@atlassiansox/analytics-web-client';
import EventEmitter from 'eventemitter3';
import * as decoding from 'lib0/decoding';
import * as encoding from 'lib0/encoding';
import * as syncProtocol from 'y-protocols/sync';
import * as Y from 'yjs';

// todo: https://product-fabric.atlassian.net/browse/CNS-10273 - Remove dependency
// eslint-disable-next-line no-restricted-imports
import { updateServerTimeOffset } from '@canvas/canvas-state/timer';
import { type Capabilities } from '@canvas/capability';
import {
  aPersonCanSaveUpdates,
  aPersonCanUseWhiteboards,
} from '@canvas/capability-summaries';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';
import { updateIndexeddbStatus, waitForHealthCheck } from '@canvas/indexeddb-state';
import { AwarenessChannel, type AwarenessClientIDs } from '@canvas/model/awareness';
import { type ClientId } from '@canvas/model/telepointers';
import { MessageType } from '@canvas/protocol';
import { isResizing, isUndoRedo } from '@canvas/protocol/messages';
import { encodeMessageV2, decodeMessageV2 } from '@canvas/protocol/v2';
import { isValidReceivedMessage } from '@canvas/protocol/validate-received-message';
import {
  type AwarenessMessage,
  type SyncPhase0Message,
  type SyncPhase1Message,
  type SyncPhase2Message,
  type UpdateMessage,
  UpdateMessageOrigin,
  type LargeUpdateMessage,
  type SessionStatusMessage,
} from '@canvas/protocol-types';
import { UnrecoverableNetworkError } from '@canvas/retry';
import {
  captureException,
  CustomerImpact,
  captureExceptionForCapability,
} from '@canvas/sentry-react';
import {
  CanvasTransactionOrigin,
  getUpdateMessageOrigin,
  type TransactionOrigin,
  YjsOrigin,
  YJS_DEFAULT,
  originIsAgent,
} from '@canvas/transaction';
import { IndexeddbPersistence } from '@canvas/yjs-providers/idb';
import { applyUpdate, safeApplyUpdate, validUpdateOrDie } from '@canvas/yjs-utils';

import { type CollabStrategy } from '../collab-strategy';
import { mergeUpdate } from '../util/merge-update';

import { type Update, type AbstractBatchedUpdates } from './abstract-batched-updates';
import { AwarenessContainer } from './awareness-container';
import { BatchedUpdates } from './batched-updates';
import { BoardDocumentMissingError, type RemoteDocument } from './document';
import { parseRelayUrl } from './document-steps';
import { FatalConnectionError } from './errors';
import { fetchLargeStep } from './large-document-steps';
import { NoopTestSocket } from './no-op-socket';
import {
  AuthenticatedSocket,
  type SocketInterface,
  type SocketFactory,
  type RelaySupplierResult,
} from './socket';
import type { Waiter } from './socket-reconnect-debounce';
import { StateBridge } from './state-bridge';
import { UpdateTracker } from './update-tracker';
import { WebSocketCodes } from './websocket-codes';

export type { Waiter };

type RemoteStepsUpdate = (
  step: string,
  attempt: number,
  signal: AbortSignal,
  collabStrategy?: CollabStrategy
) => Promise<Uint8Array[]>;

export interface YjsSocketOptions {
  autoConnect?: boolean;
  relaySupplier: (attempt: number, signal: AbortSignal) => Promise<RelaySupplierResult>;
  doc: Y.Doc;
  getDocument: (signal: AbortSignal) => Promise<RemoteDocument>;
  persistLargeStep: (step: Uint8Array<ArrayBuffer>, signal: AbortSignal) => Promise<void>;
  reconcileOfflineChanges?: (remoteDocument: RemoteDocument) => Promise<void>;
  ignoreOrigins: any[];
  updateTracker?: UpdateTracker;
  debounceWaiter?: Waiter;
  socketFactory?: SocketFactory;
  getAnalyticsWebClient?: () => Promise<AnalyticsWebClient>;
  canSync?: boolean;
  canUpdate?: boolean;
  indexedDbEnabled?: boolean;
  /**
   * Triggered when the socket is closed. Reconnects may still take place after
   * it has closed. Used this hook to execute logic after a socket close event.
   */
  onClosed?: (reason: WebSocketCodes) => void;
  boardId: string;
  getRemoteStepsUpdate: RemoteStepsUpdate;
  signal?: AbortSignal;
  getCapabilities?: () => Capabilities | undefined;
  /**
   * The version of the document to fetch.
   * If fetching a specific version, the socket will not attempt to update the
   * document with recent steps.
   */
  versionId?: string;
  /**
   * Determines whether or not version history is enabled for whiteboards or database
   */
  useVersionHistory?: boolean;
}

interface YjsSocketEvents {
  initialLoad: () => void;
  connected: () => void;
  disconnected: () => void;
  /**
   * Fired when the socket fails to connect due to the maximum collaborator count being
   * reached.
   */
  sessionFull: () => void;
  connecting: (attempt: number) => void;
  fatalError: (error: Error) => void;
  failingRetry: (error: Error) => void;
  closed: (code: WebSocketCodes) => void;
  indexedDbSynced: () => void;
}

export interface UpdateMetadata {
  /**
   * Yjs clientID associated with the update. Should be the same for an entire session,
   * however Y.js can change it on the fly if a conflict with another client ends up
   * happening.
   */
  clientID: number;
  /**
   * Yjs clock value for the above clientID at the end of the update.
   */
  clock: number;
  /**
   * The origin of the update. We just take the origin of the last received update.
   */
  origin: TransactionOrigin;
}

/**
 * Durable Objects have an inbound websocket message limit of 1 MiB (1048576 bytes)
 * @see https://developers.cloudflare.com/workers/platform/limits/#durable-objects
 */
export const LARGE_STEP_THRESHOLD = 1_048_576;

// Time allowed for local clock to be out to allow for network latency
const SERVER_TIME_DRIFT_THRESHOLD = 300;

function getDocumentId(relayUrl: string) {
  try {
    return parseRelayUrl(relayUrl).documentId;
  } catch (err) {
    return undefined;
  }
}

function encodeStateAsUpdateWithoutPendingData(
  doc: Y.Doc,
  remoteStateVector: Uint8Array
) {
  const pendingDs = doc.store.pendingDs;
  const pendingStructs = doc.store.pendingStructs;

  try {
    doc.store.pendingDs = null;
    doc.store.pendingStructs = null;

    return new Uint8Array(Y.encodeStateAsUpdate(doc, remoteStateVector));
  } finally {
    doc.store.pendingDs = pendingDs;
    doc.store.pendingStructs = pendingStructs;
  }
}

export const calculateUpdateOrigin = (originFlags: number) => {
  return isUndoRedo(originFlags)
    ? CanvasTransactionOrigin.RemoteUndoManager
    : isResizing(originFlags)
    ? CanvasTransactionOrigin.Resizing
    : YJS_DEFAULT;
};

export class YjsSocket {
  public awarenessContainer: AwarenessContainer;
  public socket: SocketInterface;
  public doc: Y.Doc;
  public readonly updateTracker: UpdateTracker;

  private stateBridge = new StateBridge();
  private batchedUpdates: AbstractBatchedUpdates<
    Uint8Array<ArrayBuffer>,
    UpdateMetadata
  > = new BatchedUpdates<Uint8Array<ArrayBuffer>, UpdateMetadata>(
    200,
    mergeUpdate.bind(this)
  );
  private batchedAwarenessUpdates = new BatchedUpdates<
    /**
     * Maps an awareness channel to the client IDs that have updated it. The client ID will
     * almost always just be the local user.
     */
    Map<AwarenessChannel, Set<number>>
  >(200, this.mergeAwareness.bind(this));

  private lastSentClock: number | null = null;
  private events: EventEmitter<YjsSocketEvents> = new EventEmitter<YjsSocketEvents>();
  private readonly getDocument: (signal: AbortSignal) => Promise<RemoteDocument>;
  private readonly ignoreOrigins: any[];
  private persistLargeStep: (
    step: Uint8Array<ArrayBuffer>,
    signal: AbortSignal
  ) => Promise<void>;
  private documentLookupFailed: boolean;
  private canSync: boolean;
  private canUpdate: boolean;
  private getRemoteStepsUpdate: RemoteStepsUpdate;
  private getCapabilities?: () => Capabilities | undefined;
  private collabStrategy?: CollabStrategy;

  constructor({
    relaySupplier,
    doc,
    getDocument,
    persistLargeStep,
    autoConnect,
    ignoreOrigins,
    updateTracker,
    debounceWaiter,
    socketFactory,
    getAnalyticsWebClient,
    canSync = true,
    canUpdate = true,
    indexedDbEnabled = true,
    boardId,
    getRemoteStepsUpdate,
    signal,
    getCapabilities,
    versionId,
    reconcileOfflineChanges,
    useVersionHistory = false,
  }: YjsSocketOptions) {
    this.awarenessContainer = new AwarenessContainer(doc);
    this.doc = doc;
    this.updateTracker = updateTracker ?? new UpdateTracker(doc.clientID);
    this.getDocument = getDocument;
    this.persistLargeStep = persistLargeStep;
    this.ignoreOrigins = [...ignoreOrigins];
    this.documentLookupFailed = false;
    this.canSync = canSync;
    this.canUpdate = canUpdate;
    this.getRemoteStepsUpdate = getRemoteStepsUpdate;
    this.getCapabilities = getCapabilities;

    const shouldSkipUpdates = () =>
      Boolean(versionId) && versionId !== 'latest' && useVersionHistory;

    this.socket =
      process.env.MODE === 'test'
        ? new NoopTestSocket()
        : new AuthenticatedSocket({
            relaySupplier,
            onSocketFetchSnapshot: this.onSocketFetchSnapshot.bind(this),
            onSocketFetchSteps: this.onSocketFetchSteps.bind(this),
            onSocketApplyChanges: this.onSocketApplyChanges.bind(this),
            onSocketUpdateChanges: this.onSocketUpdateChanges.bind(this),
            onSocketClosed: this.onSocketClosed.bind(this),
            debounceWaiter,
            shouldHaltRetry: this.shouldHaltRetry.bind(this),
            socketFactory,
            getAnalyticsWebClient,
            stateBridge: this.stateBridge,
            signal,
            shouldSkipUpdates,
            reconcileOfflineChanges: async (doc) => {
              await reconcileOfflineChanges?.(doc);
              // Ensure this update is immediately flushed to peers
              this.batchedUpdates.flush();
            },
          });

    this.socket.on('connected', this.onSocketConnect);
    this.socket.on('disconnected', this.onSocketDisconnect);
    this.socket.on('message', this.onMessage);
    this.socket.on('transientMessage', this.onTransientMessage);
    this.socket.on('failingRetry', (err: Error) => this.events.emit('failingRetry', err));
    this.socket.on('connecting', (attempt: number) =>
      this.events.emit('connecting', attempt)
    );

    this.batchedUpdates.onUpdate(this.onBatchedUpdate);
    this.batchedAwarenessUpdates.onUpdate(this.onBatchedAwarenessUpdate);
    this.doc.on('update', this.updateHandler);
    this.awarenessContainer.on('update', this.awarenessUpdateHandler);

    if (globalThis.addEventListener !== undefined) {
      globalThis.addEventListener('beforeunload', this.beforeUnloadHandler);
    } else if (typeof process !== 'undefined') {
      process.on('exit', () => this.beforeUnloadHandler);
    }

    if (autoConnect ?? true) {
      this.connect().catch((err) => {
        console.error('failed to connect', err);
        this.events.emit('fatalError', new FatalConnectionError(err));
      });
    }

    if (indexedDbEnabled && globalThis.indexedDB && !shouldSkipUpdates()) {
      waitForHealthCheck()
        .then((isHealthy) => {
          if (isHealthy) {
            const indexedDb = new IndexeddbPersistence(
              boardId,
              doc,
              updateIndexeddbStatus
            );
            this.ignoreOrigins.push(indexedDb);
            indexedDb?.on?.('synced', () => this.events.emit('indexedDbSynced'));
          }
        })
        .catch((error) => {
          captureException(error, {
            tags: {
              captureLocation: 'yjs-socket-indexeddb-healthcheck',
            },
          });
        });
    }
  }

  public async connect(): Promise<void> {
    await this.socket.connect().catch((err) => {
      console.error('failed to connect', err);
      this.events.emit('fatalError', new FatalConnectionError(err));
    });
  }

  public pause(): void {
    this.socket.pause();
  }

  public resume(): void {
    this.socket.resume();
  }

  protected shouldHaltRetry() {
    return this.documentLookupFailed;
  }

  private isNcsMode() {
    return this.collabStrategy?.type === 'ncs';
  }

  private isSyncMessage(type: MessageType) {
    return (
      type === MessageType.SyncInit ||
      type === MessageType.SyncPhase0 ||
      type === MessageType.SyncPhase1 ||
      type === MessageType.SyncPhase2
    );
  }

  private onTransientMessage = (event: Uint8Array<ArrayBuffer>) => {
    const message = decodeMessageV2(event);

    if (message.type !== MessageType.Awareness || !isValidReceivedMessage(message)) {
      captureException(
        new Error(`Received invalid transient message with type: ${message.type}`),
        {
          tags: {
            captureLocation: 'yjs-socket-invalid-transient-message',
            customerImpact: CustomerImpact.CUSTOMERS,
          },
        }
      );
      return;
    }

    this.onAwareness(message);
  };

  private onMessage = (event: Uint8Array<ArrayBuffer>) => {
    const message = decodeMessageV2(event);

    if (this.isNcsMode() && !isValidReceivedMessage(message)) {
      captureException(
        new Error(`Received invalid message with type: ${(message as any).type}`),
        {
          tags: {
            captureLocation: 'yjs-socket-invalid-received-message',
            customerImpact: CustomerImpact.CUSTOMERS,
          },
        }
      );
      return;
    }

    if (!this.checkCanSync() && this.isSyncMessage(message.type)) {
      return;
    }

    switch (message.type) {
      case MessageType.Update:
        this.onUpdate(message);
        break;
      case MessageType.LargeUpdate:
        this.onLargeUpdate(message);
        break;
      case MessageType.SyncPhase0:
        this.onSyncPhase0(message);
        break;
      case MessageType.SyncPhase1:
        this.onSyncPhase1(message);
        break;
      case MessageType.SyncPhase2:
        this.onSyncPhase2(message);
        break;
      case MessageType.Awareness:
        this.onAwareness(message);
        break;
      case MessageType.SessionStatus:
        this.onSessionStatus(message);
        break;
    }
  };

  private onSessionStatus({ data: { timestamp } }: SessionStatusMessage) {
    const offset = Date.now() - timestamp;
    if (Math.abs(offset) > SERVER_TIME_DRIFT_THRESHOLD) {
      updateServerTimeOffset(offset);
    }
  }

  private onUpdate({ data: { data, origin: originFlags } }: UpdateMessage) {
    if (!this.checkCanReceiveUpdate()) {
      return;
    }
    const origin = calculateUpdateOrigin(originFlags);

    // do not throw if received data is not valid
    // just capture the exception and leave the document unchanged
    try {
      validUpdateOrDie(data);

      this.updateTracker.received(data);

      applyUpdate(
        this.doc,
        data,
        new YjsOrigin(origin, this as unknown as TransactionOrigin)
      );
    } catch (err) {
      captureExceptionForCapability(
        err,
        {
          tags: {
            captureLocation: 'yjs-socket-on-update-catch-all',
            customerImpact: CustomerImpact.CUSTOMERS,
          },
        },
        aPersonCanSaveUpdates
      );
    }
  }

  private onLargeUpdate(message: LargeUpdateMessage) {
    if (!this.checkCanReceiveUpdate()) {
      return;
    }
    const signedDownloadUrl = message.data.data;
    fetchLargeStep(signedDownloadUrl)
      .then((response) => {
        return response.arrayBuffer();
      })
      .then((update) => {
        const binaryUpdate = new Uint8Array(update);
        this.updateTracker.received(binaryUpdate);
        applyUpdate(
          this.doc,
          binaryUpdate,
          new YjsOrigin(YJS_DEFAULT, this as unknown as TransactionOrigin)
        );
      })
      .catch((e) =>
        captureExceptionForCapability(
          e,
          {
            tags: {
              captureLocation: 'onLargeUpdate-fetch-step-failed',
              customerImpact: CustomerImpact.CUSTOMERS,
            },
          },
          aPersonCanSaveUpdates
        )
      );
  }

  private checkCanSync() {
    if (!this.getCapabilities) {
      return this.canSync;
    }
    const capabilities = this.getCapabilities();
    return capabilities?.updates.canConsume && this.canSync;
  }

  private checkCanUpdate() {
    if (!this.getCapabilities) {
      return this.canUpdate;
    }
    const capabilities = this.getCapabilities();
    return capabilities?.updates.canSend && this.canUpdate;
  }

  private checkCanReceiveUpdate() {
    if (!this.getCapabilities) {
      return true;
    }
    const capabilities = this.getCapabilities();
    return capabilities?.updates.canConsume;
  }

  private onSyncPhase0 = ({ data: { request, recipient } }: SyncPhase0Message) => {
    const decoder = decoding.createDecoder(request);
    const encoder = encoding.createEncoder();

    syncProtocol.readSyncMessage(
      decoder,
      encoder,
      this.doc,
      new YjsOrigin(YJS_DEFAULT, this as unknown as TransactionOrigin)
    );

    const syncReplyEncoder = encoding.createEncoder();
    syncProtocol.writeSyncStep1(syncReplyEncoder, this.doc);

    const message = encodeMessageV2({
      type: MessageType.SyncPhase1,
      data: {
        recipient,
        request: encoding.toUint8Array(syncReplyEncoder),
        response: encoding.toUint8Array(encoder),
      },
    });

    if (message.length > LARGE_STEP_THRESHOLD) {
      // Don't send sync message if it would cause the socket to terminate
      return;
    }

    this.socket.send(message);
  };

  private onSyncPhase1 = ({
    data: { request, response, recipient },
  }: SyncPhase1Message) => {
    const decoder = decoding.createDecoder(request);
    const encoder = encoding.createEncoder();

    syncProtocol.readSyncMessage(
      decoder,
      encoder,
      this.doc,
      new YjsOrigin(YJS_DEFAULT, this as unknown as TransactionOrigin)
    );

    syncProtocol.readSyncMessage(
      decoding.createDecoder(response),
      encoding.createEncoder(),
      this.doc,
      new YjsOrigin(YJS_DEFAULT, this as unknown as TransactionOrigin)
    );

    const message = encodeMessageV2({
      type: MessageType.SyncPhase2,
      data: {
        recipient,
        response: encoding.toUint8Array(encoder),
      },
    });

    if (message.length > LARGE_STEP_THRESHOLD) {
      // Don't send sync message if it would cause the socket to terminate
      return;
    }

    this.socket.send(message);
  };

  private onSyncPhase2 = ({ data: { response } }: SyncPhase2Message) => {
    const decoder = decoding.createDecoder(response);
    const encoder = encoding.createEncoder();

    syncProtocol.readSyncMessage(
      decoder,
      encoder,
      this.doc,
      new YjsOrigin(YJS_DEFAULT, this as unknown as TransactionOrigin)
    );
  };

  private onAwareness = (message: AwarenessMessage) => {
    this.awarenessContainer.onAwareness(message, this, this.getCapabilities?.());
  };

  private updateHandler = (
    update: Uint8Array,
    origin: any,
    _doc: Y.Doc,
    transaction: Y.Transaction
  ) => {
    if (!this.ignoreOrigins.includes(origin) && !this.isCurrentOrigin(origin)) {
      const clock =
        transaction.afterState.get(this.doc.clientID) ??
        Y.getState(this.doc.store, this.doc.clientID);

      this.batchedUpdates.addUpdate({
        payload: new Uint8Array(update),
        origin,
        clientID: this.doc.clientID,
        clock,
      });
    }
  };

  private onBatchedUpdate = (update: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>) => {
    const isLargeStep = update.payload.length > LARGE_STEP_THRESHOLD;
    const signal = new AbortController().signal;
    if (isLargeStep) {
      this.persistLargeStep(update.payload, signal)
        .then(() => {
          this.updateTracker.sent(update.payload);
        })
        .catch((err) => {
          captureExceptionForCapability(
            err,
            {
              tags: {
                captureLocation: 'batched-update-persist-large-step',
                customerImpact: CustomerImpact.CUSTOMERS,
              },
            },
            aPersonCanSaveUpdates
          );
        });
    } else {
      this.updateTracker.sent(update.payload);
      this.sendUpdate(
        update.payload,
        getUpdateMessageOrigin(update.origin),
        update.clock
      );
    }
  };

  private onBatchedAwarenessUpdate = (
    update: Update<Map<AwarenessChannel, Set<number>>>
  ) => {
    const message = encodeMessageV2(
      this.awarenessContainer.getAwarenessMessage(
        update.payload,
        this.getCapabilities?.()
      )
    );

    this.socket.sendAwareness(message);
  };

  private awarenessUpdateHandler = (
    channel: AwarenessChannel,
    { added, updated, removed }: AwarenessClientIDs,
    origin: any
  ) => {
    if (
      this.isBoardReadOnly &&
      channel !== AwarenessChannel.Telepointer &&
      channel !== AwarenessChannel.Participants &&
      channel !== AwarenessChannel.Camera &&
      channel !== AwarenessChannel.Typing
    ) {
      // Read-only users can only update their telepointer, participant state or camera positions
      return;
    }

    if (this.isCurrentOrigin(origin)) {
      return;
    }

    const changedClients = added.concat(updated).concat(removed);

    this.batchedAwarenessUpdates.addUpdate({
      payload: new Map([[channel, new Set(changedClients)]]),
    });
  };

  private beforeUnloadHandler = () => {
    this.awarenessContainer.removeLocalState(
      'window unload' as unknown as TransactionOrigin
    );
    this.batchedAwarenessUpdates.flush();
  };

  private isCurrentOrigin(origin: any) {
    const isCurrentClient =
      origin instanceof YjsOrigin &&
      origin.localOrigin === (this as unknown as TransactionOrigin);

    const isAgent = origin instanceof YjsOrigin && originIsAgent(origin.localOrigin);

    return isCurrentClient && !isAgent;
  }

  public destroy = () => {
    // Destroy batched update instances to force their interval to be cleared
    this.batchedUpdates.destroy();
    this.batchedAwarenessUpdates.destroy();

    // Destroy the socket
    this.socket.destroy();

    // Remove the beforeunload handler
    if (globalThis?.removeEventListener !== undefined) {
      globalThis.removeEventListener('beforeunload', this.beforeUnloadHandler);
    } else if (typeof process !== 'undefined') {
      process.off('exit', () => this.beforeUnloadHandler);
    }

    // Drop all event listeners
    this.updateTracker.off('dropped', this.onDroppedUpdate);
    this.updateTracker.off('roundtrip', this.onStepRoundtrip);
    this.awarenessContainer.off('update', this.awarenessUpdateHandler);
    this.doc.off('update', this.updateHandler);

    this.awarenessContainer.destroy();
    this.events.removeAllListeners();
  };

  private onSocketDisconnect = () => {
    // Remove other clients from awareness, as we've disconnected
    this.awarenessContainer.removeRemoteState(this as unknown as TransactionOrigin);

    // WebSocket has disconnected, so reset the last sent clock
    this.lastSentClock = null;

    this.events.emit('disconnected');
  };

  private onSocketConnect = () => {
    if (this.checkCanSync()) {
      const encoder = encoding.createEncoder();
      syncProtocol.writeSyncStep1(encoder, this.doc);
      this.socket.send(
        encodeMessageV2({
          type: MessageType.SyncInit,
          data: {
            data: encoding.toUint8Array(encoder),
          },
        })
      );
    }

    const awarenessChannelsLocalState = this.getInitialAwarenessState();
    if (awarenessChannelsLocalState) {
      this.socket.sendAwareness(
        encodeMessageV2(
          this.awarenessContainer.getAwarenessMessage(
            awarenessChannelsLocalState,
            this.getCapabilities?.()
          )
        )
      );
    }

    this.events.emit('connected');
  };

  private getInitialAwarenessState() {
    if (!this.isBoardReadOnly) {
      return this.awarenessContainer.getLocalStateForAllChannels();
    }

    // If the board is read-only, we only want to broadcast the telepointer, participant
    // and camera state on initial connection
    const telepointerState = this.awarenessContainer.getLocalState(
      AwarenessChannel.Telepointer
    );
    const participantState = this.awarenessContainer.getLocalState(
      AwarenessChannel.Participants
    );
    const cameraState = this.awarenessContainer.getLocalState(AwarenessChannel.Camera);
    const typingState =
      this.awarenessContainer.getLocalState(AwarenessChannel.Typing) ?? new Map();

    if (!telepointerState || !participantState || !cameraState || !typingState) {
      return;
    }

    return new Map<AwarenessChannel, Set<ClientId>>([
      ...telepointerState.entries(),
      ...participantState.entries(),
      ...cameraState.entries(),
      ...typingState.entries(),
    ]);
  }

  private onDocumentFetchFailed() {
    this.documentLookupFailed = true;
  }

  private onSocketFetchSnapshot = async (
    signal: AbortSignal
  ): Promise<RemoteDocument> => {
    try {
      const remoteDoc = await this.getDocument(signal);
      this.collabStrategy = remoteDoc.collabStrategy;

      if (this.isNcsMode()) {
        this.batchedUpdates = this.stateBridge;
      }

      return remoteDoc;
    } catch (err) {
      if (
        err instanceof UnrecoverableNetworkError ||
        err instanceof BoardDocumentMissingError
      ) {
        this.onDocumentFetchFailed();
        captureExceptionForCapability(
          err,
          {
            tags: {
              captureLocation: 'yjs-socket-document-failed-fetch',
              customerImpact: CustomerImpact.CUSTOMERS,
            },
          },
          aPersonCanUseWhiteboards
        );
      }
      throw err;
    }
  };

  private onSocketFetchSteps = async (
    remoteDoc: RemoteDocument,
    attempt: number,
    signal: AbortSignal
  ): Promise<RemoteDocument> => {
    try {
      if (remoteDoc.headStep) {
        const stepsUpdates = await this.getRemoteStepsUpdate(
          remoteDoc.headStep,
          attempt,
          signal,
          remoteDoc.collabStrategy
        );

        // apply steps from DO onto remote document
        for (const update of stepsUpdates) {
          safeApplyUpdate(remoteDoc.document, update, undefined, (err) =>
            captureExceptionForCapability(
              err,
              {
                tags: {
                  captureLocation: 'fetch-steps-error-on-apply-update',
                  customerImpact: CustomerImpact.CUSTOMERS,
                },
              },
              aPersonCanUseWhiteboards
            )
          );
        }
      }

      return remoteDoc;
    } catch (err) {
      if (err instanceof UnrecoverableNetworkError) {
        this.onDocumentFetchFailed();
        captureExceptionForCapability(
          err,
          {
            tags: {
              captureLocation: 'yjs-socket-steps-failed-fetch',
              customerImpact: CustomerImpact.CUSTOMERS,
            },
          },
          aPersonCanUseWhiteboards
        );
      }
      throw err;
    }
  };

  private onSocketApplyChanges = (relayUrl: string, remoteDoc: RemoteDocument) => {
    const localStateVector = Y.encodeStateVector(this.doc);
    const localUpdate = Y.encodeStateAsUpdate(remoteDoc.document, localStateVector);

    // apply remote changes to local doc
    Y.applyUpdate(
      this.doc,
      localUpdate,
      new YjsOrigin(YJS_DEFAULT, this as unknown as TransactionOrigin)
    );
    this.events.emit('initialLoad');

    const documentId = getDocumentId(relayUrl);
    if (this.doc.store.pendingStructs || this.doc.store.pendingDs) {
      captureException(
        new Error('Pending structs and/or deletes found in local document'),
        {
          level: 'error',
          tags: {
            captureLocation: 'apply-changes-pending-structs',
            action: 'connecting',
            documentId,
            pendingStructsMissingSize: this.doc.store.pendingStructs?.missing.size,
            pendingDeletes: this.doc.store.pendingDs?.length,
          },
        }
      );
    }

    return remoteDoc;
  };

  private onSocketUpdateChanges = (
    remoteDoc: RemoteDocument,
    connectionAttempts: number,
    signal: AbortSignal
  ): void => {
    // We don't want any pending messages at this point, as any pending updates should be
    // possible to derive from this step and the syncing step otherwise we just end up
    // with duplicate messages
    this.socket.clearPendingMessages();
    this.lastSentClock = null;

    const remoteStateVector = Y.encodeStateVector(remoteDoc.document);
    const update = encodeStateAsUpdateWithoutPendingData(this.doc, remoteStateVector);

    this.updateTracker.reset();
    this.updateTracker.on('dropped', this.onDroppedUpdate);
    this.updateTracker.on('roundtrip', this.onStepRoundtrip);

    const unsyncedChanges = Y.parseUpdateMeta(update);

    const isLargeUpdate = update.length > LARGE_STEP_THRESHOLD;
    const hasFailingConnection = connectionAttempts > 2;

    if (unsyncedChanges.to.size > 0 || unsyncedChanges.from.size > 0) {
      if (isLargeUpdate || hasFailingConnection) {
        this.persistLargeStep(update, signal).catch((ex) => {
          captureExceptionForCapability(
            ex,
            {
              tags: {
                captureLocation: 'large-step-catch-all',
                customerImpact: CustomerImpact.CUSTOMERS,
              },
            },
            aPersonCanUseWhiteboards
          );
        });
      } else {
        if (!signal.aborted) {
          this.sendUpdate(
            update,
            UpdateMessageOrigin.None,
            Y.getState(this.doc.store, this.doc.clientID)
          );
        }
      }
    }
  };

  private onSocketClosed = (code: WebSocketCodes) => {
    if (code === WebSocketCodes.SESSION_FULL) {
      this.events.emit('sessionFull');
    }
  };

  private onDroppedUpdate = () => {
    if (this.socket?.open) {
      console.warn('Detected unacknowledged updates, restarting socket');
      this.socket.restart();
    }
  };

  // TODO: CNS-23550 pass collabType dynamically based on collab provider being used (cloudflare vs ncs)
  private onStepRoundtrip = (sentAt: number, receivedAt: number) => {
    ReliabilityMetrics.getInstance().successWithDuration(
      MetricName.STEP_ROUNDTRIP,
      sentAt,
      receivedAt,
      { collabType: 'cloudflare' }
    );
  };

  addListener = this.events.addListener.bind(this.events);
  removeListener = this.events.removeListener.bind(this.events);

  private sendUpdate(payload: Uint8Array, origin: UpdateMessageOrigin, clock: number) {
    this.socket.send(
      encodeMessageV2({
        type: MessageType.Update,
        data: {
          data: payload,
          origin: origin,
          previousYjsClock: this.lastSentClock,
          currentYjsClock: clock,
        },
      }),
      this.checkCanUpdate()
    );
    this.lastSentClock = clock;
  }

  private mergeAwareness(
    updates: Update<Map<AwarenessChannel, Set<number>>>[]
  ): Update<Map<AwarenessChannel, Set<number>>> {
    const channelToClientIdMaps = updates.map((update) => update.payload);
    const mergedChannelToClientIdMap: Map<AwarenessChannel, Set<number>> = new Map();

    for (const channelToClientIdMap of channelToClientIdMaps) {
      for (const [channel, clientIds] of channelToClientIdMap) {
        const mergedClientIds = mergedChannelToClientIdMap.get(channel) ?? new Set();

        for (const clientId of clientIds) {
          mergedClientIds.add(clientId);
        }

        mergedChannelToClientIdMap.set(channel, mergedClientIds);
      }
    }

    return {
      payload: mergedChannelToClientIdMap,
    };
  }

  get state() {
    return this.socket.state;
  }

  flushUpdates() {
    this.batchedUpdates.flush();
    this.batchedAwarenessUpdates.flush();
  }

  private get isBoardReadOnly() {
    return !this.canUpdate;
  }
}
